import { useContext, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router";
import { LoginService } from "@propertelligent/client-api";
import { UserLoginData } from "../components/common/charts/Enums";
import { MyContext } from "../utils/context/LoginContext";
import Localizer from "../utils/i18nConfig";
import { URLS, USER_ROLES, USERS } from "../constants/constants";
import "./UserName.css";
import { NotificationType, PubSub, TopicsEnum } from "../utils/pubsub";
import { GetImages } from "../utils/GetImages";
import dropdownIcon from "../assets/chevren-down.svg";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { PermissionContext } from "../utils/context/PermissionContext";
import NameAvatarComponent from "../components/tenants/NameAvatarComponent";

const UserName = ({ size, userRoleId }) => {
  const { isLogin, setIsLogin } = useContext(MyContext);
  const permissionContext = useContext(PermissionContext);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const fullName = sessionStorage.getItem(UserLoginData.user_name) || "";
  const nameParts = fullName.split(/\s+/).filter(Boolean);

  const firstName = nameParts[0] || "";
  const lastName = nameParts[1] || "";

  // Helper check for any Org permissions
  const perms = permissionContext?.permissions || {};
  const hasOrgAccess = 
    (perms["OrganizationDashboard"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["PaymentAccounts"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["PayeeAccounts"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["ManageUsers"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["Reports"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["Templates"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly;

  // Helper check for any Platform Settings permissions
  const hasPlatformAccess = 
    (perms["Subscription"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["MyProfile"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["Feedback"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly ||
    (perms["About"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly;


  const navigateToOrganization = () => {
    // Basic check for Overview
    const perms = permissionContext?.permissions || {};
    if ((perms["OrganizationDashboard"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) {
       navigate('/organization/new-overview');
       return;
    }

    // Checking remaining organization tabs in order of appearance in sidebar
    if ((perms["PaymentAccounts"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/organization/new-payment-accounts'); return; }
    if ((perms["PayeeAccounts"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/organization/new-payee-accounts'); return; }
    if ((perms["ManageUsers"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/organization/new-manage-users'); return; }
    if ((perms["Reports"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/organization/reports'); return; }
    if ((perms["Templates"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/organization/templates'); return; }
    
    // Total fallback 
    navigate('/organization/new-overview'); 
  };

  const navigateToPlatformSettings = () => {
    const perms = permissionContext?.permissions || {};
    if ((perms["Subscription"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/platform-settings/mysubscription'); return; }
    if ((perms["Feedback"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/platform-settings/Feedback'); return; }
    if ((perms["About"] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly) { navigate('/platform-settings/about'); return; }

    // Total fallback
    navigate('/platform-settings/mysubscription');
  };

  // My Settings is available to every role (not permissioned). Org users get a My Profile tab
  // (moved here from the Platform Settings area) in its left rail, so their default landing tab
  // is My Profile; tenants/contractors have no My Profile tab here, so they default to Notifications.
  const navigateToMySettings = () => {
    navigate(`${URLS.MY_SETTINGS}/notification-settings`);
  };

  const navigateToOrgMySettings = () => {
    navigate(`${URLS.MY_SETTINGS}/myprofile`);
  };

  const navigateToProfile = () => {
    navigate(URLS.MY_PROFILE);
  };

  // Platform super-admin: "My Settings" opens the profile-only settings page; "Propertelligent"
  // opens the cross-org support-requests queue.
  const navigateToPlatformMySettings = () => {
    navigate(URLS.MY_SETTINGS);
  };

  // Platform super-admin "Platform Settings" — the sub-shell hosting Payment Accounts, Support Requests, and
  // Organizations. Opens to Payment Accounts.
  const navigateToPlatformAdminSettings = () => {
    navigate(URLS.PLATFORM_SETTINGS_PAYMENT_ACCOUNTS);
  };

  const logoutFunction = () => {
    PubSub.publish(TopicsEnum.Logout.toString(), {
      message: "Successfully logged out",
      type: NotificationType.Success
    });
    LoginService.putApiLoginLogout()
      .then((res) => {
        sessionStorage.clear();
        setIsLogin(false);
        setTimeout(() => {
          navigate(URLS.HOME, {
            state: {
              isComingFromLogout: true,
            },
          });
        }, 1000);
      })
      .catch((error) => { });
  };

  return (
  <div className="appBarRightSide">
    <Dropdown className="avatarDropdown" onToggle={(show) => setIsOpen(show)}>
      <Dropdown.Toggle
        variant="link"
      id="dropdown-avatar"
        className="avatarOnlyToggle bg-transparent border d-flex align-items-center gap-2 px-3 py-2 rounded username-border"
      >
        <div className="profilePicStyles d-flex align-items-center gap-1">
          <NameAvatarComponent
            firstName={firstName}
            lastName={lastName}
            heightWidth={28}
            fromTopBar={true}
            fontSize="12px"
            color="var(--Primary-Regular)"
            backgroundColor="var(--Primary-Lighter)"
          />
          <img
            src={dropdownIcon}
            alt="arrow"
            width={25}
            height={25}
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
              filter: "var(--icon-filter)"
            }}
          />
        </div>
      </Dropdown.Toggle>

        <Dropdown.Menu className="dropdownBoxUsername">
        {USERS.USER === USER_ROLES[userRoleId] ? (
          <>
            {hasOrgAccess && (
              <Dropdown.Item
                className="d-flex gap-2 itemFromUsername align-items-center"
                onClick={navigateToOrganization}
              >
                <img className="username-item-icon" width={20} height={20} src={GetImages.organizationSetting} />
                <p className="normal_text fw-500 m-0">
                  {Localizer.getLocalizedString("my_org")}
                </p>
              </Dropdown.Item>
            )}

            {/* My Settings — between My Organization and Propertelligent; not permissioned. */}
            <Dropdown.Item
              className="d-flex gap-2 itemFromUsername align-items-center"
              onClick={navigateToOrgMySettings}
            >
              <img className="username-item-icon" width={20} height={20} src={GetImages.notificatioIcon} />
              <p className="normal_text fw-500 m-0">My Settings</p>
            </Dropdown.Item>

            {hasPlatformAccess && (
              <Dropdown.Item
                className="d-flex gap-2 itemFromUsername align-items-center"
                onClick={navigateToPlatformSettings}
              >
                <img className="username-item-icon" width={20} height={20} src={GetImages.Propertelligent} />
                <p className="normal_text fw-500 m-0">
                  {Localizer.getLocalizedString("platform_settings")}
                </p>
              </Dropdown.Item>
            )}
          </>
        ) : USERS.PLATFORM === USER_ROLES[userRoleId] ? (
          <>
            {/* Platform: My Settings (profile only) + Platform Settings (Payment Accounts / Support / Orgs). */}
            <Dropdown.Item
              className="d-flex gap-2 itemFromUsername align-items-center"
              onClick={navigateToPlatformMySettings}
            >
              <img className="username-item-icon" width={20} height={20} src={GetImages.notificatioIcon} />
              <p className="normal_text fw-500 m-0">My Settings</p>
            </Dropdown.Item>
            <Dropdown.Item
              className="d-flex gap-2 itemFromUsername align-items-center"
              onClick={navigateToPlatformAdminSettings}
            >
              <img className="username-item-icon" width={20} height={20} src={GetImages.Propertelligent} />
              <p className="normal_text fw-500 m-0">Platform Settings</p>
            </Dropdown.Item>
          </>
        ) : USERS.TENANT === USER_ROLES[userRoleId] ? (
          <>
            <Dropdown.Item
              className="d-flex gap-2 itemFromUsername align-items-center"
              onClick={navigateToProfile}
            >
              <img className="username-item-icon" width={20} height={20} src={GetImages.organizationSetting} />
              <p className="normal_text fw-500 m-0">My Profile</p>
            </Dropdown.Item>
            <Dropdown.Item
              className="d-flex gap-2 itemFromUsername align-items-center"
              onClick={navigateToMySettings}
            >
              <img className="username-item-icon" width={20} height={20} src={GetImages.notificatioIcon} />
              <p className="normal_text fw-500 m-0">My Settings</p>
            </Dropdown.Item>
          </>
        ) : (
          <Dropdown.Item
            className="d-flex gap-2 itemFromUsername align-items-center"
            onClick={navigateToMySettings}
          >
            <img className="username-item-icon" width={20} height={20} src={GetImages.notificatioIcon} />
            <p className="normal_text fw-500 m-0">My Settings</p>
          </Dropdown.Item>
        )}

        <Dropdown.Item
          className="d-flex gap-2 itemFromUsername align-items-center"
          onClick={logoutFunction}
        >
          <img className="username-item-icon" width={20} height={20} src={GetImages.logout} />
          <p className="normal_text fw-500 m-0">
            {Localizer.getLocalizedString("logout")}
          </p>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

};

export default UserName;
